import EditIcon from '@mui/icons-material/Edit';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { departmentOptions } from '../../common/enums.tsx';
import { defaultInclusionInfo, PatientSummary } from '../../store/patientsSlice';

interface PatientSummaryProps {
  patient: PatientSummary;
}

export const PatientStatusChip: React.FC<PatientSummaryProps> = ({ patient }) => {
  return patient.inclusionCompleted ? (
    patient.accountValidated ? (
      <Tooltip title={`Activé le ${patient.accountValidatedDate && format(new Date(patient.accountValidatedDate), "dd MMM yyyy HH'h'mm")}`}>
        <Chip color="success" label="Activé" />
      </Tooltip>
    ) : (
      <Tooltip title="Information inclusion entrées, utilisateur non activé">
        <Chip color="primary" label="Inclus" />
      </Tooltip>
    )
  ) : (
    <Tooltip title="Compte créé, information d'inclusion à ajouter">
      <Chip label="Créé" sx={{ backgroundColor: 'primary.light', color: 'primary.main' }} />
    </Tooltip>
  );
};
interface PatientInclusionInfoProps {
  patient: PatientSummary;
  editMode: boolean;
}

export const PatientInclusionInfo: React.FC<PatientInclusionInfoProps> = ({ patient, editMode }) => {
  const navigate = useNavigate();
  const inclusionInfo = patient.inclusionInfo ? patient.inclusionInfo : defaultInclusionInfo;

  const getDepartmentName = (code: string) => {
    const department = departmentOptions.find((option) => option.code === code);
    return department ? department.name : code;
  };

  const getRelationshipDegreeText = (degree: number | null) => {
    switch (degree) {
      case 1:
        return 'premier';
      case 2:
        return 'second';
      case 3:
        return 'troisieme';
      default:
        return degree;
    }
  };
  const handleEdit = (step: number) => {
    navigate(`/patient/${patient.id}/complete/${step.toString()}`);
  };

  return (
    <>
      <Grid container sx={{ mt: 2 }}>
        <Grid size={3}>
          <Stack gap={2} sx={{ m: 1, p: 2, height: '100%', backgroundColor: 'primary.light', borderRadius: '10px' }}>
            {editMode && (
              <Box sx={{ position: 'relative', top: -10, right: -10 }}>
                <IconButton
                  onClick={() => {
                    handleEdit(0);
                  }}
                  sx={{ position: 'absolute', top: 0, right: 0, p: 0 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Typography align="center" sx={{ fontSize: '1rem', color: 'primary.main', fontWeight: 'bold', mt: 1, mb: 1 }}>
              Informations générales
            </Typography>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                👶 Année de naissance&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {' '}
                {inclusionInfo.yearOfBirth}
              </Typography>
            </Box>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                🧑 Sexe&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {' '}
                {inclusionInfo.sex}
              </Typography>
            </Box>

            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                📏 Taille&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {' '}
                {inclusionInfo.height}&nbsp;cm
              </Typography>
            </Box>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                ⚖️ Poids&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {' '}
                {inclusionInfo.weight}&nbsp;kg
              </Typography>
            </Box>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                💍 Statut marital&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {inclusionInfo.maritalStatus}
              </Typography>
            </Box>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                👔 Statut pro&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {' '}
                {inclusionInfo.professionalStatus}
              </Typography>
            </Box>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                🌍 Département&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {' '}
                {getDepartmentName(inclusionInfo.deptCode)}
              </Typography>
            </Box>
          </Stack>
        </Grid>

        <Grid size={3}>
          <Stack gap={2} sx={{ m: 1, p: 2, height: '100%', backgroundColor: 'primary.light', borderRadius: '10px' }}>
            {editMode && (
              <Box sx={{ position: 'relative', top: -10, right: -10 }}>
                <IconButton
                  onClick={() => {
                    handleEdit(1);
                  }}
                  sx={{ position: 'absolute', top: 0, right: 0, p: 0 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Typography align="center" sx={{ fontSize: '1rem', color: 'primary.main', fontWeight: 'bold', mt: 1, mb: 1 }}>
              Conditions particulières
            </Typography>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                🍹 Consommation d'alcool&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {' '}
                {inclusionInfo.alcohol ? `${inclusionInfo.alcoholUnits} unités par semaine` : 'Non'}
              </Typography>
            </Box>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                💊 Consommation de substances&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {' '}
                {inclusionInfo.substances ? `${inclusionInfo.substancesType.join(', ')}, ${inclusionInfo.substancesFrequency}` : 'Non'}
              </Typography>
            </Box>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                🚬 Consommation de tabac&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {' '}
                {inclusionInfo.tobacco ? inclusionInfo.tobaccoFrequency : 'Non'}
              </Typography>
            </Box>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                🤰 Grossesse&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {' '}
                {inclusionInfo.pregnancy ? 'Oui' : 'Non'}
              </Typography>
            </Box>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                🍼 Allaitement&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {' '}
                {inclusionInfo.breastfeeding ? 'Oui' : 'Non'}
              </Typography>
            </Box>
          </Stack>
        </Grid>
        <Grid size={3}>
          <Stack gap={2} sx={{ m: 1, p: 2, height: '100%', backgroundColor: 'primary.light', borderRadius: '10px' }}>
            {editMode && (
              <Box sx={{ position: 'relative', top: -10, right: -10 }}>
                <IconButton
                  onClick={() => {
                    handleEdit(2);
                  }}
                  sx={{ position: 'absolute', top: 0, right: 0, p: 0 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Typography align="center" sx={{ fontSize: '1rem', color: 'primary.main', fontWeight: 'bold', mt: 1, mb: 1 }}>
              Caractéristiques cliniques
            </Typography>

            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                🤒 Comorbidités&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {Object.keys(inclusionInfo.comorbidities)
                  .filter((key) => inclusionInfo.comorbidities[key])
                  .map((key) => (key.startsWith('comorbidity') ? key.replace('comorbidity', '') : key))
                  .join(', ')
                  .toLowerCase() || 'No'}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.875rem', mb: 1 }}>💊 Traitements&nbsp;:&nbsp;</Typography>
              {inclusionInfo.medications.map((med, index) => (
                <Tooltip
                  key={index}
                  title={`${med.name} ${med.quantity.toString()}${med.unit}, ${med.frequency.toString()} x par jour, débuté en ${format(new Date(med.startDate), 'MMM yy')}.`}
                >
                  <Chip
                    key={index}
                    label={`${med.name} ${med.quantity.toString()}${med.unit}, ${med.frequency.toString()} x par jour, débuté en ${format(new Date(med.startDate), 'MMM yy')}.`}
                    size="small"
                    sx={{ color: 'primary.main', backgroundColor: 'primary.light', mb: 1, fontWeight: 'medium', borderColor: 'primary.main' }}
                    variant="outlined"
                  />
                </Tooltip>
              ))}
            </Box>
          </Stack>
        </Grid>
        <Grid size={3}>
          <Stack gap={2} sx={{ m: 1, p: 2, height: '100%', backgroundColor: 'primary.light', borderRadius: '10px' }}>
            {editMode && (
              <Box sx={{ position: 'relative', top: -10, right: -10 }}>
                <IconButton
                  onClick={() => {
                    handleEdit(3);
                  }}
                  sx={{ position: 'absolute', top: 0, right: 0, p: 0 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Typography align="center" sx={{ fontSize: '1rem', color: 'primary.main', fontWeight: 'bold', mt: 1, mb: 1 }}>
              Historique de la maladie
            </Typography>

            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                👶 Antécédents familiaux&nbsp;:&nbsp;
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {inclusionInfo.familyPsychiatricDisorder ? '' : 'Non'}
                {inclusionInfo.familyPsychiatricDisorder
                  ? inclusionInfo.familyPsychiatricDisorders.join(', ') +
                    ' (' +
                    getRelationshipDegreeText(inclusionInfo.familyPsychiatricDisorderRelationshipDegree) +
                    ' degré)'
                  : ''}
              </Typography>
            </Box>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                📖 Épisodes antérieurs&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {inclusionInfo.episodes}
                {inclusionInfo.relapsed ? ', avec rechute' : ''}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.875rem', mb: 1 }}>🏥 Hospitalisations&nbsp;:&nbsp;</Typography>
              {inclusionInfo.hospitalizations && inclusionInfo.hospitalizations.length > 0 ? (
                inclusionInfo.hospitalizations.map((hospital, index) => (
                  <Tooltip
                    key={index}
                    title={`${hospital.hospital}: ${hospital.reason}, ${format(new Date(hospital.startDate), 'MMM yy')} - ${format(new Date(hospital.endDate), 'MMM yy')}`}
                  >
                    <Chip
                      key={index}
                      label={`${hospital.hospital}: ${hospital.reason}, ${format(new Date(hospital.startDate), 'MMM yy')} - ${format(new Date(hospital.endDate), 'MMM yy')}`}
                      size="small"
                      sx={{ color: 'primary.main', backgroundColor: 'primary.light', mb: 1, fontWeight: 'medium', borderColor: 'primary.main' }}
                      variant="outlined"
                    />
                  </Tooltip>
                ))
              ) : (
                <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                  Non
                </Typography>
              )}
            </Box>
            <Box>
              <Typography display="inline" sx={{ fontSize: '0.875rem' }}>
                😞 Tentative de suicide&nbsp;:&nbsp;
              </Typography>
              <Typography display="inline" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                {inclusionInfo.suicideAttempts}
              </Typography>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
};

export default PatientInclusionInfo;
